import i18nFactory from '@yandex-int/i18n';
import { Link } from 'lego-on-react';
import * as React from 'react';
import { memo, MutableRefObject, useCallback, useRef, useState } from 'react';

import {
    getPropsByStatus,
    mapIconIframe,
    mapStatusTextIframe
} from '../../mappers/brandlift/brandlift';
import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BrandSurveyStatus, SurveyStatus } from '../../typings/campaigns';
import { GdCampaignPrimaryStatus } from '../../typings/graphql-grid';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { Tab, TabList, TabPanel, Tabs, TabsSize } from '../Tabs/Tabs';

import './BrandLiftIFrame.css';
import * as keyset from './BrandLiftIFrame.i18n';

const i18n = i18nFactory(keyset);

export interface BrandLiftIFrameProps {
    isVisible?: boolean;
    clientId: string;
    status: GdCampaignPrimaryStatus;
    brandSurveyStatus: BrandSurveyStatus;
    brandSurveyId: string;
    onClose: () => void;
}

enum BrandLiftCanvasMessage {
    save = 'save'
}

const BLOCK = 'brand-lift-iframe';

const getSrc = (clientId: string, brandSurveyId: string): [string, string] => {
    const iframeSrc = UrlHelpers.getCanvasBrandLiftPreviewUrl(
        clientId,
        brandSurveyId
    );
    const iframeStatisticSrc = UrlHelpers.getCanvasBrandLiftPreviewStatisticUrl(
        clientId,
        brandSurveyId
    );

    return [iframeSrc, iframeStatisticSrc];
};

const useMessageIframe = (
    isVisible: boolean
): [MutableRefObject<HTMLIFrameElement | null>, () => void] => {
    const modal = useRef<HTMLIFrameElement>(null);

    const onMessage = (
        contentWindow: WindowProxy,
        type: BrandLiftCanvasMessage
    ) => {
        contentWindow.postMessage({ type }, '*');
    };

    const messageDecorator = (type: BrandLiftCanvasMessage) => () => {
        if (
            !isVisible ||
            !modal ||
            !modal.current ||
            !modal.current.contentWindow
        ) {
            return;
        }

        onMessage(modal.current.contentWindow, type);
    };

    const onSaveMessage = messageDecorator(BrandLiftCanvasMessage.save);

    return [modal, onSaveMessage];
};

const getHeader = (
    onClose: () => void,
    brandSurveyStatus: BrandSurveyStatus,
    status: GdCampaignPrimaryStatus
) => {
    const { surveyStatus } = brandSurveyStatus;
    const { icon, tone } = mapIconIframe(surveyStatus);
    const { title, text } = mapStatusTextIframe(brandSurveyStatus, status);
    const propertyByStatus = getPropsByStatus(surveyStatus);

    return (
        <div className={Bem.cls(BLOCK, 'header')}>
            <div className={Bem.cls(BLOCK, 'header-title')}>
                <div className={Bem.cls(BLOCK, 'header-logo')}>
                    <Shape2 icon={Shape2Icon.vision} width={28} height={28} />
                    <Shape2
                        icon={Shape2Icon.visionRuText}
                        width={130}
                        height={30}
                    />
                </div>
                <div className={Bem.cls(BLOCK, 'header-text')}>
                    {i18n('brandlift')}
                </div>
                <Link
                    cls={Bem.cls(BLOCK, 'close')}
                    theme="pseudo"
                    onClick={onClose}
                >
                    <Shape2 icon={Shape2Icon.close} width={15} height={15} />
                </Link>
            </div>
            <div
                className={Bem.cls({
                    block: BLOCK,
                    elem: 'notification-header',
                    mods: { [propertyByStatus]: true }
                })}
            >
                <Shape2
                    cls={Bem.cls(BLOCK, 'notification-icon')}
                    icon={icon}
                    tone={tone}
                    hoverable={true}
                    width={18}
                    height={18}
                />
                <span className={Bem.cls(BLOCK, 'notification-title')}>
                    {title}
                </span>
                &nbsp;
                {text}
            </div>
        </div>
    );
};

const getBody = (
    modal: MutableRefObject<HTMLIFrameElement | null>,
    iframeSrc: string,
    iframeStatisticSrc: string,
    surveyStatus: SurveyStatus
) => {
    const [selectIndex, setSelect] = useState(0);

    switch (surveyStatus) {
        case SurveyStatus.ACTIVE:
        case SurveyStatus.COMPLETED:
            return (
                <div className={Bem.cls(BLOCK, 'body')}>
                    <Tabs
                        size={TabsSize.m}
                        selectedIndex={selectIndex}
                        onSelect={setSelect}
                        selectedTabPanelClassName={Bem.cls(
                            BLOCK,
                            'tab-panel--selected'
                        )}
                    >
                        <TabList className={Bem.cls(BLOCK, 'tab-list')}>
                            <Tab>{i18n('params')}</Tab>
                            <Tab>{i18n('result')}</Tab>
                        </TabList>
                        <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                            <iframe
                                ref={modal}
                                className={Bem.cls(BLOCK, 'iframe')}
                                frameBorder="0"
                                src={iframeSrc}
                            />
                        </TabPanel>
                        <TabPanel className={Bem.cls(BLOCK, 'tab-panel')}>
                            <iframe
                                className={Bem.cls(BLOCK, 'iframe')}
                                frameBorder="0"
                                src={iframeStatisticSrc}
                            />
                        </TabPanel>
                    </Tabs>
                </div>
            );
        default:
            return (
                <div className={Bem.cls(BLOCK, 'body')}>
                    <iframe
                        ref={modal}
                        className={Bem.cls(BLOCK, 'iframe')}
                        frameBorder="0"
                        src={iframeSrc}
                    />
                </div>
            );
    }
};

const getFooter = (
    surveyStatus: SurveyStatus,
    onCancel: () => void,
    onSave: () => void
) => {
    return (
        <div className={Bem.cls(BLOCK, 'footer')}>
            <ButtonDecorator
                theme="action"
                size="n"
                cls={Bem.cls(BLOCK, 'save-btn')}
                disabled={surveyStatus !== SurveyStatus.DRAFT}
                onClick={onSave}
            >
                {i18n('save')}
            </ButtonDecorator>

            <ButtonDecorator
                theme="normal"
                size="n"
                cls={Bem.cls(BLOCK, 'cancel-btn')}
                onClick={onCancel}
            >
                {i18n('cancel')}
            </ButtonDecorator>
        </div>
    );
};

export const BrandLiftIFrame = memo(
    ({
        isVisible,
        onClose,
        clientId,
        brandSurveyStatus,
        status,
        brandSurveyId
    }: BrandLiftIFrameProps) => {
        const { surveyStatus } = brandSurveyStatus;
        const getIframeSrc = useCallback(getSrc, [clientId, brandSurveyId]);
        const [iframeSrc, iframeStatisticSrc] = getIframeSrc(
            clientId,
            brandSurveyId
        );

        const [modal, onSaveMessage] = useMessageIframe(Boolean(isVisible));

        return (
            <ModalDecorator
                visible={Boolean(isVisible)}
                theme={'clear'}
                cls={Bem.cls(BLOCK, 'modal')}
                onOutsideClick={onClose}
                bodyCls={Bem.cls(BLOCK, 'modal-body')}
            >
                {getHeader(onClose, brandSurveyStatus, status)}
                {getBody(modal, iframeSrc, iframeStatisticSrc, surveyStatus)}
                {getFooter(surveyStatus, onClose, onSaveMessage)}
            </ModalDecorator>
        );
    }
);
